import {
  EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_EDGE_GAP_PX,
  measureEmailBuilderMergeTagAutocompleteSafeBounds,
  type IEmailBuilderMergeTagAutocompleteSafeBounds,
} from "./emailBuilderMergeTagMatching";

export const EMAIL_BUILDER_TEXT_LINK_POPOVER_WIDTH_PX = 280;
export const EMAIL_BUILDER_TEXT_LINK_POPOVER_GAP_PX = EMAIL_BUILDER_MERGE_TAG_AUTOCOMPLETE_EDGE_GAP_PX;
export const EMAIL_BUILDER_TEXT_LINK_POPOVER_PREFERRED_MAX_HEIGHT_PX = 320;

export type TEmailBuilderTextLinkPopoverPlacement = "below" | "beside-right";

export interface IEmailBuilderTextLinkPopoverPosition {
  left: number;
  top: number;
  maxWidth: number;
  maxHeight: number;
}

type TEmailBuilderTextLinkPopoverAnchorRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export function resolveEmailBuilderTextLinkPopoverPosition(args: {
  anchorRect: TEmailBuilderTextLinkPopoverAnchorRect;
  placement: TEmailBuilderTextLinkPopoverPlacement;
  safeBounds?: IEmailBuilderMergeTagAutocompleteSafeBounds;
}): IEmailBuilderTextLinkPopoverPosition {
  const gap = EMAIL_BUILDER_TEXT_LINK_POPOVER_GAP_PX;
  const safeBounds =
    args.safeBounds ??
    measureEmailBuilderMergeTagAutocompleteSafeBounds({
      width: typeof window !== "undefined" ? window.innerWidth : 0,
      height: typeof window !== "undefined" ? window.innerHeight : 0,
    });
  const preferredWidth = EMAIL_BUILDER_TEXT_LINK_POPOVER_WIDTH_PX;
  const preferredMaxHeight = EMAIL_BUILDER_TEXT_LINK_POPOVER_PREFERRED_MAX_HEIGHT_PX;

  if (args.placement === "beside-right") {
    const spaceRight = Math.max(0, safeBounds.right - (args.anchorRect.right + gap));
    const spaceLeft = Math.max(0, args.anchorRect.left - gap - safeBounds.left);
    const placeRight = spaceRight >= Math.min(preferredWidth, 160) || spaceRight >= spaceLeft;

    let left: number;
    let maxWidth: number;
    if (placeRight) {
      left = args.anchorRect.right + gap;
      maxWidth = Math.min(preferredWidth, spaceRight);
    } else {
      maxWidth = Math.min(preferredWidth, spaceLeft);
      left = args.anchorRect.left - gap - maxWidth;
    }

    if (maxWidth <= 0) {
      maxWidth = Math.min(preferredWidth, Math.max(0, safeBounds.right - safeBounds.left));
      left = safeBounds.left;
    } else {
      left = Math.max(safeBounds.left, Math.min(left, safeBounds.right - maxWidth));
    }

    const spaceBelow = Math.max(0, safeBounds.bottom - args.anchorRect.top);
    const maxHeight = Math.min(preferredMaxHeight, spaceBelow);
    let top = args.anchorRect.top;
    if (top + maxHeight > safeBounds.bottom) {
      top = Math.max(safeBounds.top, safeBounds.bottom - maxHeight);
    }

    return { left, top, maxWidth, maxHeight };
  }

  const availableWidth = Math.max(0, safeBounds.right - safeBounds.left);
  const maxWidth = Math.min(preferredWidth, availableWidth);
  let left = args.anchorRect.left;
  if (left + maxWidth > safeBounds.right) {
    left = safeBounds.right - maxWidth;
  }
  left = Math.max(safeBounds.left, left);

  const spaceBelow = Math.max(0, safeBounds.bottom - (args.anchorRect.bottom + gap));
  const spaceAbove = Math.max(0, args.anchorRect.top - gap - safeBounds.top);
  const placeBelow = spaceBelow >= Math.min(preferredMaxHeight, 120) || spaceBelow >= spaceAbove;

  if (placeBelow) {
    return {
      left,
      top: args.anchorRect.bottom + gap,
      maxWidth,
      maxHeight: Math.min(preferredMaxHeight, spaceBelow),
    };
  }

  const maxHeight = Math.min(preferredMaxHeight, spaceAbove);
  return {
    left,
    top: Math.max(safeBounds.top, args.anchorRect.top - gap - maxHeight),
    maxWidth,
    maxHeight,
  };
}
